<!-- ServerIconIsometric.vue -->
<template>
  <div class="isometric-server" :class="sizeClass" @click="handleClick">
    <div class="server-card isometric" :class="{ 'no-click': !clickable }">
      <svg class="server-icon" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient :id="'topGrad' + instanceId" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" :style="`stop-color:${topColor};stop-opacity:1`" />
            <stop offset="100%" :style="`stop-color:${topColorEnd};stop-opacity:1`" />
          </linearGradient>
          <linearGradient :id="'frontGrad' + instanceId" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" :style="`stop-color:${frontColor};stop-opacity:1`" />
            <stop offset="100%" :style="`stop-color:${frontColorEnd};stop-opacity:1`" />
          </linearGradient>
          <linearGradient :id="'sideGrad' + instanceId" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" :style="`stop-color:${sideColor};stop-opacity:1`" />
            <stop offset="100%" :style="`stop-color:${sideColorEnd};stop-opacity:1`" />
          </linearGradient>
        </defs>

        <!-- 等轴测服务器 - 顶面 -->
        <polygon 
          points="100,25 155,50 100,75 45,50" 
          :fill="`url(#topGrad${instanceId})`" 
          :opacity="topOpacity"
        />
        <polygon 
          points="100,25 155,50 100,75 45,50" 
          fill="none" 
          :stroke="borderColor" 
          stroke-width="1" 
          :opacity="borderOpacity"
        />

        <!-- 顶面网格 -->
        <g :opacity="gridOpacity">
          <line x1="70" y1="38" x2="130" y2="62" stroke="#fff" stroke-width="0.5"/>
          <line x1="85" y1="32" x2="115" y2="56" stroke="#fff" stroke-width="0.5"/>
          <line x1="55" y1="44" x2="145" y2="68" stroke="#fff" stroke-width="0.5"/>
          <line x1="60" y1="50" x2="75" y2="38" stroke="#fff" stroke-width="0.5"/>
          <line x1="140" y1="50" x2="125" y2="38" stroke="#fff" stroke-width="0.5"/>
        </g>

        <!-- 顶面指示灯 -->
        <circle cx="85" cy="40" r="2" :fill="ledColor1">
          <animate attributeName="opacity" values="1;0.2;1" dur="1s" repeatCount="indefinite"/>
        </circle>
        <circle cx="115" cy="40" r="2" :fill="ledColor2">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite"/>
        </circle>

        <!-- 等轴测服务器 - 正面 -->
        <polygon 
          points="45,50 100,75 100,150 45,125" 
          :fill="`url(#frontGrad${instanceId})`" 
          :stroke="frontBorderColor" 
          stroke-width="1"
        />

        <!-- 正面数据条 -->
        <g :transform="`translate(55, ${70 + dataOffset})`" :opacity="dataOpacity">
          <rect x="0" y="0" width="35" height="4" rx="1" fill="rgba(255,255,255,0.03)"/>
          <rect x="0" y="0" width="25" height="4" rx="1" :fill="dataColor1" :opacity="0.7">
            <animate attributeName="width" values="20;30;15;25;20" dur="3s" repeatCount="indefinite"/>
          </rect>
          <rect x="0" y="8" width="35" height="4" rx="1" fill="rgba(255,255,255,0.03)"/>
          <rect x="0" y="8" width="28" height="4" rx="1" :fill="dataColor2" :opacity="0.7">
            <animate attributeName="width" values="25;32;18;28;25" dur="3.5s" repeatCount="indefinite"/>
          </rect>
          <rect x="0" y="16" width="35" height="4" rx="1" fill="rgba(255,255,255,0.03)"/>
          <rect x="0" y="16" width="18" height="4" rx="1" :fill="dataColor3" :opacity="0.6">
            <animate attributeName="width" values="30;15;22;18;30" dur="4s" repeatCount="indefinite"/>
          </rect>
        </g>

        <!-- 正面端口 -->
        <g :transform="`translate(55, ${105 + portOffset})`" :opacity="portOpacity">
          <rect x="0" y="0" width="8" height="6" rx="1" fill="rgba(0,0,0,0.3)" :stroke="portBorder" stroke-width="0.5"/>
          <rect x="12" y="0" width="8" height="6" rx="1" fill="rgba(0,0,0,0.3)" :stroke="portBorder" stroke-width="0.5"/>
          <rect x="24" y="0" width="8" height="6" rx="1" fill="rgba(0,0,0,0.3)" :stroke="portBorder" stroke-width="0.5"/>
          <rect x="36" y="0" width="8" height="6" rx="1" fill="rgba(0,0,0,0.3)" :stroke="portBorder" stroke-width="0.5"/>
        </g>

        <!-- 等轴测服务器 - 侧面 -->
        <polygon 
          points="100,75 155,50 155,125 100,150" 
          :fill="`url(#sideGrad${instanceId})`" 
          :stroke="sideBorderColor" 
          stroke-width="1"
        />

        <!-- 侧面散热纹 -->
        <g :opacity="ventOpacity">
          <line 
            v-for="i in 6" 
            :key="i" 
            :x1="115" 
            :y1="70 + (i-1) * 8" 
            :x2="140" 
            :y2="58 + (i-1) * 8" 
            stroke="#00D4FF" 
            stroke-width="0.5"
          />
        </g>

        <!-- 侧面发光条 -->
        <line x1="140" y1="55" x2="140" y2="120" :stroke="glowColor" stroke-width="1" :opacity="0.3">
          <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite"/>
        </line>

        <!-- 底部阴影 -->
        <ellipse cx="100" cy="165" rx="50" ry="8" fill="rgba(0,0,0,0.3)"/>
      </svg>

      <!-- 标签 -->
      <!-- <div class="server-label" v-if="showLabel">{{ label }}</div> -->
    </div>
  </div>
</template>

<script>
let idCounter = 0

export default {
  name: 'ServerIconIsometric',
  props: {
    // ===== 尺寸 =====
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v)
    },
    
    // ===== 颜色 =====
    topColor: {
      type: String,
      default: '#00D4FF'
    },
    topColorEnd: {
      type: String,
      default: '#7C3AED'
    },
    frontColor: {
      type: String,
      default: '#1a1a2e'
    },
    frontColorEnd: {
      type: String,
      default: '#0d0d1a'
    },
    sideColor: {
      type: String,
      default: '#2D3561'
    },
    sideColorEnd: {
      type: String,
      default: '#1a1a2e'
    },
    
    // ===== 透明度 =====
    topOpacity: {
      type: Number,
      default: 0.8
    },
    borderOpacity: {
      type: Number,
      default: 0.6
    },
    gridOpacity: {
      type: Number,
      default: 0.2
    },
    dataOpacity: {
      type: Number,
      default: 1
    },
    portOpacity: {
      type: Number,
      default: 0.5
    },
    ventOpacity: {
      type: Number,
      default: 0.15
    },
    
    // ===== 颜色配置 =====
    borderColor: {
      type: String,
      default: '#00D4FF'
    },
    frontBorderColor: {
      type: String,
      default: 'rgba(0,212,255,0.05)'
    },
    sideBorderColor: {
      type: String,
      default: 'rgba(0,212,255,0.03)'
    },
    portBorder: {
      type: String,
      default: 'rgba(0,212,255,0.05)'
    },
    glowColor: {
      type: String,
      default: '#00D4FF'
    },
    
    // ===== LED 颜色 =====
    ledColor1: {
      type: String,
      default: '#00FF88'
    },
    ledColor2: {
      type: String,
      default: '#FFD700'
    },
    
    // ===== 数据条颜色 =====
    dataColor1: {
      type: String,
      default: '#00D4FF'
    },
    dataColor2: {
      type: String,
      default: '#7C3AED'
    },
    dataColor3: {
      type: String,
      default: '#FFD700'
    },
    
    // ===== 偏移量 =====
    dataOffset: {
      type: Number,
      default: 0
    },
    portOffset: {
      type: Number,
      default: 0
    },
    
    // ===== 标签 =====
    label: {
      type: String,
      default: 'ISOMETRIC SERVER'
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    
    // ===== 点击事件 =====
    clickable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 生成唯一实例 ID
      instanceId: `_${(++idCounter).toString(36)}`
    }
  },
  computed: {
    sizeClass() {
      return `size-${this.size}`
    }
  },
  methods: {
    handleClick() {
      if (this.clickable) {
        this.$emit('click')
      }
    }
  }
}
</script>

<style scoped>
.isometric-server {
  display: inline-block;
}

.server-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  /* padding: 16px; */
  /* background: rgba(255, 255, 255, 0.02); */
  /* border-radius: 16px; */
  /* border: 1px solid rgba(255, 255, 255, 0.04); */
  transition: all 0.4s ease;
  cursor: pointer;
  user-select: none;
}

.server-card:hover:not(.no-click) {
  transform: translateY(-6px);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 0 12px 40px rgba(124, 58, 237, 0.08);
  /* background: rgba(255, 255, 255, 0.04); */
}

.server-card:active:not(.no-click) {
  transform: scale(0.95);
}

.server-card.no-click {
  cursor: default;
}

.server-card.no-click:hover {
  transform: none;
}

.server-icon {
  display: block;
  width: 100%;
  height: auto;
}

.server-label {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.2);
  letter-spacing: 2px;
  font-family: 'Courier New', monospace;
}

/* ===== 尺寸 ===== */
.size-sm .server-card {
  padding: 0px;
  gap: 8px;
}

.size-sm .server-icon {
  max-width: 80px;
}

.size-sm .server-label {
  font-size: 9px;
}

.size-md .server-card {
  padding: 16px;
  gap: 12px;
}

.size-md .server-icon {
  max-width: 150px;
}

.size-lg .server-card {
  padding: 20px;
  gap: 14px;
}

.size-lg .server-icon {
  max-width: 200px;
}

.size-lg .server-label {
  font-size: 13px;
}

.size-xl .server-card {
  padding: 24px;
  gap: 16px;
}

.size-xl .server-icon {
  max-width: 250px;
}

.size-xl .server-label {
  font-size: 14px;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .size-lg .server-icon {
    max-width: 150px;
  }
  
  .size-xl .server-icon {
    max-width: 180px;
  }
}
</style>